import { Badge, Flex, TagLabel, TagLeftIcon, Text } from "@chakra-ui/react";
import { useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { IconType } from "@/common/components/Icons";
import { TextWordBreak } from "@/common/components/TextWordBreak";
import { useDebounce } from "@/common/hooks/useDebounce";
import { useErrorToast } from "@/common/hooks/useErrorToast";
import { useFormFieldDiff } from "@/common/hooks/useFormFieldDiff";
import { useFormFieldUpdate } from "@/common/hooks/useFormFieldUpdate";
import {
  AttributeField,
  AttributeFilterOperator,
  convertDateValueOperator,
  defaultTypeValues,
  isDayOperator,
  listOperators,
  operatorDisplayName,
  operatorsWithoutValue,
  systemAttributeFields,
  TypeByOperator,
  userSystemAttributeFields,
} from "@reflag/shared/attributeFilter";
import { SEARCH_DEBOUNCE_MS } from "@reflag/shared/constants";
import {
  AdjustAttributeFieldsCallback,
  CompanyAttributeFilter,
  UIFilterType,
} from "@reflag/shared/filter";

import {
  AttributeSourceEntityType,
  useAttributeNames,
} from "../../data/useAttributeNames";
import { ManagedFormControl } from "../Form/ManagedFormControl";
import RuleAttribute from "../Rule/RuleAttribute";
import RuleOperator from "../Rule/RuleOperator";
import RuleValue, {
  FieldType,
  getFieldTypeFromAttributeType,
} from "../Rule/RuleValue";
import { attributeOperators } from "./attributeOperators";
import FilterValue from "./FilterValue";

type AttributeFilterItemProps = {
  filter: Omit<CompanyAttributeFilter, "type">;
  prefix?: string;
  icon?: IconType;
  compact?: boolean;
  noWrap?: boolean;
};

export function AttributeFilterItem({
  filter,
  icon,
  compact = false,
  prefix,
  noWrap = false,
}: AttributeFilterItemProps) {
  const systemAttribute = [
    ...systemAttributeFields,
    ...userSystemAttributeFields,
  ].find((field) => field.key === filter.field);

  const label = systemAttribute?.label ?? filter.field;
  const size = "sm"; // compact ? "xs" : "sm";

  return (
    <>
      {icon && (
        <TagLeftIcon
          as={icon}
          color="dimmed"
          h="auto"
          height="16px"
          ml="-1px"
          mr={compact ? 1 : 1.5}
          w="auto"
          width="16px"
        />
      )}
      <TagLabel noOfLines={4} whiteSpace="normal">
        <Flex columnGap={compact ? 1 : 2} flexWrap={noWrap ? "nowrap" : "wrap"}>
          {prefix && (
            <Badge color="dimmed" colorScheme="blackAlpha" size={size}>
              {compact ? prefix[0] : prefix}
            </Badge>
          )}
          <TextWordBreak as="span" fontSize={size} noWrap={noWrap}>
            {label}
          </TextWordBreak>
          <Text as="span" color="dimmed" fontSize={size}>
            {operatorDisplayName[filter.operator]}
          </Text>
          <FilterValue
            noWrap={noWrap}
            operator={filter.operator}
            size={size}
            type={systemAttribute?.type}
            value={filter.values}
          />
        </Flex>
      </TagLabel>
    </>
  );
}

const getValueType = (
  attribute: AttributeField | undefined,
  operator: CompanyAttributeFilter["operator"],
): FieldType => {
  if (operatorsWithoutValue.includes(operator)) return "none";
  if (listOperators.includes(operator)) return "list";

  return getFieldTypeFromAttributeType(
    // An observed type must not change an existing rule's input or saved value.
    attribute?.typeIsInferred || attribute?.type === "array"
      ? TypeByOperator[operator]
      : (attribute?.type ?? TypeByOperator[operator]),
  );
};

type AttributeFilterFieldsProps = {
  type: Extract<
    UIFilterType,
    "companyAttribute" | "userAttribute" | "otherContext"
  >;
  adjustAttributeFields?: AdjustAttributeFieldsCallback;
};

const attributeSourceEntityTypeMap: Record<
  Extract<UIFilterType, "companyAttribute" | "userAttribute" | "otherContext">,
  AttributeSourceEntityType
> = {
  companyAttribute: "company",
  userAttribute: "feature-targeting-context-user",
  otherContext: "feature-targeting-context-other",
};

export function AttributeFilterFields({
  type,
  adjustAttributeFields,
}: AttributeFilterFieldsProps) {
  const form = useFormContext<CompanyAttributeFilter>();
  const attributeKey = useWatch({ name: "field" });
  const debouncedAttributeKey = useDebounce(attributeKey, SEARCH_DEBOUNCE_MS);
  const {
    attributes: all,
    isLoading: isAttributesLoading,
    isError: isAttributesError,
  } = useAttributeNames(
    attributeSourceEntityTypeMap[type],
    debouncedAttributeKey,
  );

  const errorToast = useErrorToast();

  useEffect(() => {
    if (isAttributesError) {
      errorToast({ title: "Failed to load attributes" });
    }
  }, [isAttributesError, errorToast]);

  const attributes = useMemo(
    () => adjustAttributeFields?.(type, all) || all,
    [adjustAttributeFields, all, type],
  );

  const operator = useWatch<{ operator: AttributeFilterOperator }>({
    name: "operator",
  });
  const attribute = attributes.find((field) => field.key === attributeKey);

  const valueType = getValueType(attribute, operator);
  const operators = attributeOperators(
    attribute?.type,
    operator,
    attribute?.typeIsInferred,
  );

  // The field changed. Reset the operator to default if the field type does not support the current operator.
  useFormFieldUpdate(form, "field", (newValue) => {
    if (form.getValues("type") !== type) {
      return;
    }

    const currentOperator = form.getValues("operator");
    const attribute = attributes.find((field) => field.key === newValue);

    const attributeType = attribute?.type;
    // Observed types are advisory, so they must not reset the operator or values.
    if (
      attributeType &&
      attributeType !== "array" &&
      !attribute?.typeIsInferred
    ) {
      // Use the new field's menu, not the previous array field's empty groups.
      const nextOperators = attributeOperators(attributeType)[attributeType];
      if (!nextOperators.includes(currentOperator)) {
        form.setValue("operator", nextOperators[0]);
      }
    }
  });

  // The operator changed. Reset value to default if the value type also changed.
  useFormFieldDiff(
    form,
    "operator",
    ({ oldValue: oldOperator, newValue: newOperator }) => {
      if (form.getValues("type") !== type) return;
      const valueType = TypeByOperator[newOperator];
      if (valueType === "segment" || valueType === "featureTargeting") return;
      const previousValueType = oldOperator && TypeByOperator[oldOperator];
      if (valueType !== previousValueType) {
        form.setValue("values", defaultTypeValues(valueType, newOperator), {
          shouldDirty: true,
        });
      } else if (isDayOperator(oldOperator) !== isDayOperator(newOperator)) {
        const currentValue = form.getValues("values");
        const newValues = convertDateValueOperator(
          oldOperator,
          newOperator,
          currentValue ?? [""],
        );
        form.setValue("values", newValues, {
          shouldDirty: true,
        });
      } else {
        // Validate value field, since the new operator might not work
        // form the existing value
        form.trigger("values");
      }
    },
  );

  return (
    <>
      <ManagedFormControl
        name="field"
        render={({ field }) => (
          <RuleAttribute
            {...field}
            attributes={attributes}
            isLoading={isAttributesLoading}
          />
        )}
      />

      <ManagedFormControl
        name="operator"
        render={({ field }) => (
          <RuleOperator {...field} operators={operators} />
        )}
      />

      <ManagedFormControl
        key={`${attributeKey}.${operator}`}
        // Operators without an input must not register a fictitious first value.
        name={
          listOperators.includes(operator) ||
          operatorsWithoutValue.includes(operator)
            ? "values"
            : "values[0]"
        }
        render={({ field }) => {
          return (
            <RuleValue
              {...field}
              attributeKey={attribute ? attributeKey : undefined}
              entityType={attributeSourceEntityTypeMap[type]}
              fieldType={valueType}
              operator={operator}
            />
          );
        }}
      />
    </>
  );
}
